import deffered from 'yaDeferred';
import getRelayEnvironment from '../relayClientEnvironment';
import * as constants from '../authModal/authentication/authFlowConstants';
import { steps as postAuthSteps } from '../authModal/postAuth/constants';
import asyncAuth from '../authModal/async';
import LoadingModal from '../sharedModalComponents/LoadingModal';
import React from 'react';
import { getInitialState } from '../authModal/authentication/authReducer';

let Auth;
let AuthEnv;
let AuthContent;

let authModalLoader = {
    _onLoaded: deffered(),

    /**
     * Splits the AuthModal to a separate chunk during build time. Loads the authModal when called.
     * For now there's no way to handle errors. Should be possible when we switch to webpack 2.
     * This is mostly an espace hatch if you need direct access to the authModal instance.
     *
     * @example
     * authModalLoader.load().then(AuthModal => DO STUFF)
     *
     * @returns {Promise} - A promise that resolves with the AuthModal module.
     */
    load() {
        asyncAuth().then(mod => {
            Auth = mod;
            authModalLoader._onLoaded.resolve(mod);
        });
        return authModalLoader._onLoaded;
    },

    /**
     * Usefull when you don't want to load the AuthModal but want to get the instance.
     *
     * @example
     * authModalLoader.getLoadPromise().then(AuthModal => DO STUFF)
     *
     * @returns {Promise} - A promise that resolves with the AuthModal module.
     */
    getLoadPromise() {
        return authModalLoader._onLoaded;
    },

    /**
     * returns true or false depending upon whether the current auth modal instance is in a paused state
     * for more info about pausing see `shouldPauseAuthFlow in` authentication/readme.md
     */
    isPaused() {
        if (!Auth) {
            return false;
        }
        return Auth.getAuthModalState().isPaused;
    },

    /**
     * returns true or false depending upon whether the current auth content instance is in a paused state
     * for more info about pausing see `shouldPauseAuthFlow in` authentication/readme.md
     */
    isContentPaused() {
        if (!Auth) {
            return false;
        }
        return Auth.getAuthContentState().isPaused;
    },

    /**
     * resumes the auth flow wherever it last left off (which is derived from the savedState field in the auth reducer)
     * similar functionality to `show`, but uses the already existing Auth instance instead of initializing a new one
     */
    resume() {
        if (!Auth) {
            throw new Error(
                'Auth Modal cannot resume if it has not been started... use `authModalLoader.show` instead!'
            );
        }
        const savedState = Auth.getAuthModalState().savedState;
        return new Promise(resolve => {
            this._launchAuth({
                action: savedState.modal,
                options: { ...savedState, step: savedState.step + 1 },
                resolve,
            });
        }).then(authResult => {
            const isRegister = authResult.modal === constants.REGISTER;
            //LoginMutation and RegistrationMutation passes isNewUser, newUser deprecated
            authResult = { ...authResult, newUser: isRegister };
            return authResult;
        });
    },

    /**
     * get current auth instance (like getLoadPromise, but no need to go through callbacks to acquire instance)
     */
    getInstance() {
        return Auth;
    },

    /**
     * Shortcut to AuthModal.show.
     *
     * @example
     * authModalLoader.show(options).then((authResponse) => if (authResponse.err) FAILURE else SUCCESS);
     * authResponse.user will be an object containing user data: user.userId, user.userToken, user.profile {}.
     * authResponse.err will be an object with shape {err: 'closed'}.
     * @returns {Promise}
     */
    show(options = {}) {
        const environment = options.relayEnvironment || getRelayEnvironment();
        return new Promise(resolve => {
            this.load().then(AuthModal => {
                AuthModal.initAuth({ environment });
                this._launchAuth({
                    action: options.action,
                    options: { ...getInitialState(), isPaused: false, ...options },
                    resolve,
                });
            });
        }).then(authResult => {
            const isRegister = authResult.modal === constants.REGISTER;
            //LoginMutation and RegistrationMutation passes isNewUser, newUser deprecated
            authResult = { ...authResult, newUser: isRegister };
            return authResult;
        });
    },

    /**
     * Launches postAuth with specified configuration
     *
     * @example
     * authModalLoader.showPostAuth(options).then(() => DO STUFF);
     * @param {Object} options.props - get passed to PostAuthModal component
     * @param {String} options.step - which post auth flow to run ../authModal/postAuth/constants
     * @param {Object} options.relayEnvironment
     * @param {String} options.flow
     * @param {String} options.tradeApplicationSource - (optional) used as leadSourceDetails variable for Trade Applications
     *
     * @returns {Promise} - resolved when modal is closed
     */
    showPostAuth(options) {
        const {
            props = {},
            step = postAuthSteps.USER_TYPE_SELECTION,
            relayEnvironment,
            postAuthFlow,
            authFlow,
            tradeApplicationSource,
            launchOrigin,
        } = options;
        const environment = relayEnvironment || AuthEnv || getRelayEnvironment();

        return new Promise(resolve => {
            this.load().then(postAuth => {
                postAuth.init(environment, props);
                if (postAuthFlow || authFlow) {
                    postAuth.run(postAuthFlow, authFlow, launchOrigin);
                }
                if (tradeApplicationSource) {
                    postAuth.setTradeApplicationSource(tradeApplicationSource);
                }
                postAuth.show(resolve);
                postAuth.goToStep(step);
            });
        });
    },

    _launchAuth({ action, options, resolve }) {
        switch (action) {
            case constants.LOGIN:
                Auth.launchLogin({ ...options, resolve });
                break;
            case constants.RESET_PASSWORD:
                Auth.launchResetPassword({ ...options, resolve });
                break;
            case constants.UPDATE_PASSWORD:
                Auth.launchUpdatePassword({ ...options, resolve });
                break;
            case constants.REQUEST_PASSWORD_EMAIL:
                Auth.launchRequestPasswordEmail({ ...options, resolve });
                break;
            case constants.SINGLE_STEP_REGISTRATION:
                Auth.launchSingleStepRegistration({ ...options, resolve });
                break;
            default:
                Auth.launchRegistration({ ...options, resolve });
                break;
        }
    },

    /**
     * method for resuming CONTENT ONLY auth, the difference being we don't want to wrap the launching in a promise like we do in the other above `resume` method
     * because we are passing in a resolve method from the consumer in this case
     * @param {*} options options to resume content with -- if empty, it will just used savedState
     */
    _resumeAuthContent(options) {
        if (!Auth) {
            throw new Error(
                'Auth Modal cannot resume if it has not been started... use `authModalLoader.show` instead!'
            );
        }
        const savedState = Auth.getAuthContentState().savedState;
        this._launchAuth({
            action: savedState.modal,
            options: {
                ...savedState,
                step: savedState.step + 1,
                isPaused: false,
                ...options,
            },
            resolve: options.onAuthComplete,
        });
    },

    /**
     * method for updating the auth store with any generic payload
     * @param {*} payload payload to update store with
     */
    _updateAuthStore(payload) {
        Auth.updateAuthStore(payload);
    },

    /**
     * serves the same purpose as `authModalLoader.show`, but returns the auth content as a fragment instead of launching it within it's own modal container
     * returns the AuthContentRenderer wrapper in redux provider (within React.Suspense if it hasn't been loaded yet, but caches for future loads)
     * for an example of usage, see `ContactDealerModalContent.jsx` and `ContactDealer.jsx`
     * @param {*} options object containing config for the auth modal-- functions the same as the options in `show` above
     */
    loadAuthContent(options = {}) {
        const environment = options.relayEnvironment || AuthEnv || getRelayEnvironment();
        if (AuthContent && Auth) {
            if (this.isContentPaused() && options.resume) {
                this._resumeAuthContent(options);
            } else {
                this._launchAuth({
                    action: options.action,
                    options: { ...getInitialState(), ...options, isContentOnly: true },
                    resolve: options.onAuthComplete,
                });
            }
            return <AuthContent environment={environment} />;
        }

        AuthContent =
            AuthContent ||
            React.lazy(() =>
                import(
                    /* webpackChunkName: "AuthProvider" */
                    '../authModal/authentication/AuthProvider'
                ).then(module => {
                    Auth = module;
                    this._launchAuth({
                        action: options.action,
                        options: { ...getInitialState(), ...options, isContentOnly: true },
                        resolve: options.onAuthComplete,
                    });
                    return module;
                })
            );

        return (
            <React.Suspense fallback={<LoadingModal />}>
                <AuthContent environment={environment} />
            </React.Suspense>
        );
    },

    /**
     * expose the contents closing functionality to the consumer
     * resets auth store to initial state
     */
    closeAuthContent() {
        if (Auth) {
            Auth.closeAuthContent();
        }
    },

    /**
     * Used for testing only.
     */
    _clearAuthModal() {
        Auth = undefined;
        authModalLoader._onLoaded = deffered();
    },

    /**
     * Used for testing only.
     */
    _setAuthModal(m) {
        Auth = m;
    },
};

authModalLoader = Object.assign(authModalLoader, { constants });

export default authModalLoader;
